// A show URL carries `{slug}-{id}` — e.g. `sugar-baby-94`. The slug is there for
// humans and crawlers; the trailing numeric id is what actually resolves the
// show. That is the whole point of the shape: no slug→id map has to be shipped
// or kept fresh, a renamed show keeps resolving, and a `/{id}/{episode}` link
// already in circulation (ad campaigns, past shares, history) still works
// because a bare numeric id is accepted as-is — see parseSeriesParam.
//
// The slug is built from `show.title`, the SOURCE field, never `displayTitle`:
// `displayTitle` is resolved from Accept-Language, so a French client would
// build `/vengeance-sur-mon-fiance-94` while the prerendered file on disk is
// `/revenge-on-my-cheating-fiance-94`, breaking the mapping for 8 locales out
// of 9. Same identity rule the tracking payloads follow (see `.claude/rules/seo-meta-tags.md`): a URL
// is an identity, not rendered copy.

// Greedy `.*` so the LAST numeric group wins: "twisted-fates-2-94" → "94".
// The slug is optional: show ids are numeric, so a bare "94" is an id too.
// Anything else — `/video/0`, `/serie-player/0`, a link a mail client
// truncated — is no show at all: a client 404, not a `GetShow` the API answers
// with `Invalid show ID` and that Sentry then reports as an error
// (SHORTS-WEB-5V, ~7k events).
const SERIES_PARAM = /^(?:.*-)?(\d+)$/;

export const toSlug = (title: string): string =>
  title
    .normalize("NFD")
    // Combining marks left by NFD: "é" → "e". The range is U+0300 to
    // U+036F, named in prose and written as escapes on purpose: a combining
    // mark has no glyph of its own and attaches to whatever character
    // precedes it, so a literal one written here would be invisible in the
    // source and would silently corrupt the character before it.
    .replace(/[\u0300-\u036f]/g, "")
    // Straight and curly apostrophes, removed rather than replaced, so
    // "Billionaire's" gives one word. Written as escapes on purpose: a
    // literal U+2019 here was silently transcribed as U+0027 once already.
    .replace(/[\u0027\u2019]/g, "")
    .replace(/&/g, " and ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const buildSeriesParam = (
  title: string | undefined,
  showId: string,
): string => {
  const slug = title ? toSlug(title) : "";
  return slug ? `${slug}-${showId}` : showId;
};

export const parseSeriesParam = (param: string): string | null =>
  SERIES_PARAM.exec(param)?.[1] ?? null;

/**
 * The canonical show URL: `{origin}/{slug}-{id}/0`.
 *
 * The single expression for "a show URL handed to the outside world" — the
 * `canonical`/`og:url` tags (client runtime and build-time prerender alike) and
 * both share sheets. Never `window.location.href`: every episode now has a
 * prerendered file of its own (`vite/prerenderShows.ts`), so it would unfurl
 * correctly — but it carries whatever the router last wrote, query string
 * included, where a shared link should carry the canonical form the OG tags on
 * that page themselves declare.
 *
 * Pinned to episode `0` on purpose (SHO-664): every episode of a show carries
 * an identical title/description, so a distinct canonical per episode would
 * read as duplicate content to Google.
 *
 * It is deliberately NOT what the address bar carries while episodes play —
 * that has to follow the episode being watched. `EpisodesPlayer.onSlideChange`
 * owns it and reuses the `series` segment the visitor arrived on verbatim
 * rather than rebuilding one here; see the comment there for why rebuilding is
 * the wrong move.
 */
export const buildShowUrl = (
  origin: string,
  title: string | undefined,
  showId: string,
): string => `${origin}/${buildSeriesParam(title, showId)}/0`;

/**
 * The series details page URL: `{origin}/series/{slug}-{id}` (SHO-757).
 *
 * Its own `/series/` namespace so it can never compete with the player's
 * root-level `/{series}/{episode}`. The page's canonical and its Share button.
 */
export const buildSeriesDetailsUrl = (
  origin: string,
  title: string | undefined,
  showId: string,
): string => `${origin}/series/${buildSeriesParam(title, showId)}`;
